import React, { useCallback } from 'react';

import {
  SvgTrash,
  CustomSelect,
  FormItem,
  TertiaryBtn,
  Input,
  MultiSelect,
} from '@tmapy/style-guide';

import { useFormItemServerErrors, useInput, useMultiSelect, useSelect } from '../../hooks';
import { filterByText } from '../../utils/filterByText';
import type { ServerErrors } from '../../types';

import type { NestedPublishsetDatasetResponse, PatchedDatasetResponse } from '../../openapi/types';
import { datasetsList } from '../../openapi/operations/datasetsList';

type PublishsetDataset = NestedPublishsetDatasetResponse;

/**
 * uid - pomocne id pro nove vytvarene prvky
 * -> nejsou fyzicky ulozene, ale jiz se s nimi ve formulari pracuje a lze je nasledne odebirat
 * -> aby nedochazelo k neocekavanemu chovani
 */
export type PublishsetDatasetItem = Omit<PublishsetDataset, 'id'> & {
  id?: number;
  uid?: number;
  serverErrors?: ServerErrors;
};

type PublishsetDatasetListItemProps = {
  uid: number;
  publishsetDataset: PublishsetDatasetItem;
  onChange(uid: number, newPublishsetDataset: PublishsetDatasetItem): void;
  onDelete(uid: number): void;
};

const renderDatasetItem = (item: PatchedDatasetResponse) => item.name;
const renderFieldItem = (item: string) => item;

export const PublishsetDatasetListItem: React.FC<PublishsetDatasetListItemProps> = ({
  uid,
  publishsetDataset,
  onChange,
  onDelete,
}) => {
  const formItemServerErrors = useFormItemServerErrors(publishsetDataset.serverErrors);

  const handleChange = (newElementData: Partial<PublishsetDataset>) => {
    const name = Object.keys(newElementData)?.[0];
    if (publishsetDataset && publishsetDataset.serverErrors?.[name]) {
      delete publishsetDataset.serverErrors[name];
    }

    const newPublishsetDataset = {
      id: publishsetDataset.id,
      uid: publishsetDataset.uid,
      serverErrors: publishsetDataset.serverErrors,
      dataset: dataset.value,
      fields: fields.values,
      condition: condition.value,
      ...newElementData,
    };
    onChange(uid, newPublishsetDataset);
  };

  const prepareDatasetItems = useCallback(async (search: string) => {
    const response = await datasetsList({ query: { name__icontains: search } });
    if (response.ok) {
      return response.data!.results;
    }
    return null;
  }, []);

  const dataset = useSelect({
    name: 'dataset',
    initialValue: publishsetDataset.dataset,
    onChange: (dataset) => {
      const newValues = dataset
        ? fields.values.filter((value) => dataset.fields?.indexOf(value) !== -1)
        : [];
      fields.setValues(newValues);
      fields.refresh();
      handleChange({ dataset });
    },
    prepareItems: prepareDatasetItems,
    renderValue: (item) => item.name ?? '',
    renderItem: renderDatasetItem,
    ...formItemServerErrors.dataset,
  });

  const prepareFieldsItems = useCallback(
    (search: string) => {
      return filterByText(search, dataset.value?.fields ?? []);
    },
    [dataset.value],
  );

  const fields = useMultiSelect({
    name: 'fields',
    initialValue: publishsetDataset.fields ?? [],
    prepareItems: prepareFieldsItems,
    isDisabled: !dataset.value,
    renderValue: (item) => item,
    renderItem: renderFieldItem,
    onChange: (_, eventData) => {
      handleChange({ fields: eventData?.values });
    },
    ...formItemServerErrors.fields,
  });

  const condition = useInput({
    name: 'condition',
    initialValue: publishsetDataset.condition,
    isDisabled: !dataset.value,
    onChange: (_, eventData) => {
      handleChange({ condition: eventData?.value });
    },
    ...formItemServerErrors.condition,
  });

  const handleDelete = () => {
    onDelete(uid);
  };

  return (
    <>
      <div>
        <FormItem {...dataset}>
          <CustomSelect {...dataset} />
        </FormItem>
      </div>
      <div>
        <FormItem {...fields}>
          <MultiSelect {...fields} />
        </FormItem>
      </div>
      <div>
        <FormItem {...condition}>
          <Input {...condition} />
        </FormItem>
      </div>
      <div>
        <TertiaryBtn icon={{ element: <SvgTrash /> }} isDanger onClick={handleDelete} />
      </div>
    </>
  );
};
